import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Button, Space, Tag, Typography } from 'antd';
import { DetailField, DetailPage, useCapability, useConfirmAction } from '@viq-admin/ui';
import { QuestionEdit } from './edit';
import { typeLabel } from './list';
import { getQuestion, deleteQuestion } from '../../api/discovery';
import type { DiscoveryQuestion } from '../../api/types';

const { Text } = Typography;

/**
 * Discovery question detail — read-only by default, Edit flips the form in
 * place (via the shared DetailPage). One route, /questions/:id. Mirrors the
 * console `QuestionDetail`'s structure and read view COLUMN-FOR-COLUMN — a
 * divergence between the two apps is a bug. Only the fetch mechanism and the
 * access gate differ:
 *
 *  - Fetch: the portal is single-tenant/token-scoped (no "operate as tenant"
 *    concept), so this hand-rolls `getQuestion(id)` in a `useEffect` instead
 *    of the console's `useOne` — there is no companyId to thread and no
 *    "select a tenant" branch. `adminFetch` throws an `HttpError` with
 *    `statusCode` on a non-2xx response (see `api/dataProvider.ts`); derive
 *    `notFound` from it exactly like the console does from `useOne`'s
 *    `query.error`, and wire `notFound`/`notFoundHref` into `DetailPage` the
 *    same way.
 *  - Gate: the ROUTE is wrapped `RoleGuard allow={['company_admin']}` in
 *    App.tsx (portal has no read-only persona — `read`/`manage` on
 *    DiscoveryQuestion both map to `company_admin`; `stakeholder` never
 *    reaches this component). `editCap`/the header Delete action still gate
 *    on the capability string (`manage:DiscoveryQuestion`) for parity with
 *    the console's capability-driven header, even though in practice every
 *    company_admin who reaches this page already has it.
 *
 * The read view surfaces the FULL ordered options list (the list column only
 * shows a truncated preview) plus two flags invisible on the list today:
 * `hasOtherOption` and the free-text "no options" note. `typeLabel` is
 * imported from `list.tsx` rather than duplicated (same as the console).
 *
 * Delete lives in the header (gated `manage:DiscoveryQuestion`), in addition
 * to — not instead of — the list row's kebab Delete. Deletion is the portal's
 * hand-rolled `deleteQuestion(id)` (NOT a Refine mutation), then navigate to
 * the list — mirroring the list row's own delete action.
 *
 * Unlike the console (whose `useOne` query cache auto-invalidates after
 * `useUpdate`, so the read view reflects a save with zero extra code), this
 * component's fetch and the embedded `QuestionEdit`'s fetch are two
 * independent hand-rolled requests with no shared cache — `onSaved` below
 * explicitly refetches so the read view shows the just-saved record instead
 * of a stale one.
 */
export function QuestionDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [question, setQuestion] = useState<DiscoveryQuestion | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const confirm = useConfirmAction();
  const canManage = useCapability('manage:DiscoveryQuestion');

  const refresh = useCallback(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    getQuestion(id)
      .then((q) => setQuestion(q))
      .catch((err) => setError(err))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const notFound = (error as { statusCode?: number } | null)?.statusCode === 404;

  function handleDelete() {
    confirm({
      title: 'Delete this question?',
      okText: 'Delete',
      danger: true,
      successMessage: 'Question deleted',
      onConfirm: async () => {
        await deleteQuestion(id!);
        navigate('/questions');
      },
    });
  }

  const headerActions = canManage ? (
    <Button danger onClick={handleDelete}>
      Delete
    </Button>
  ) : null;

  const readView = question ? (
    <Space
      direction="vertical"
      size="large"
      style={{ width: '100%' }}
      data-testid="question-read-view"
    >
      <DetailField label="Question" value={question.text} />
      <DetailField label="Type" value={typeLabel(question)} />
      <DetailField
        label="Required"
        value={
          question.required ? <Tag color="gold">Required</Tag> : <Tag>Optional</Tag>
        }
      />
      <DetailField
        label="Default"
        value={question.isDefault ? <Tag>★ Default</Tag> : '—'}
      />
      {question.type === 'choice' && (
        <DetailField
          label='Includes "Other: ___"'
          value={question.hasOtherOption ? 'Yes' : 'No'}
        />
      )}
      <DetailField
        label="Answer options"
        value={
          question.type === 'free_text' ? (
            <Text type="secondary">
              No options. The rep types a free-form answer on the tear sheet — ideal
              for grabbing a cell phone, a referral name, or a one-off detail.
            </Text>
          ) : (
            <ol style={{ margin: 0, paddingLeft: 20 }}>
              {[...question.options]
                .sort((a, b) => a.sortOrder - b.sortOrder)
                .map((o) => (
                  <li key={o.id}>{o.text}</li>
                ))}
            </ol>
          )
        }
      />
      <DetailField label="Created" value={new Date(question.createdAt).toLocaleDateString()} />
      <DetailField label="Updated" value={new Date(question.updatedAt).toLocaleDateString()} />
    </Space>
  ) : null;

  return (
    <DetailPage
      title={question?.text ?? 'Question'}
      breadcrumbs={[
        { label: 'Questions Inventory', href: '/questions' },
        { label: question?.text ?? '' },
      ]}
      onNavigate={({ href }) => navigate(href)}
      onBack={() => navigate('/questions')}
      loading={loading}
      error={notFound ? undefined : error}
      notFound={notFound}
      notFoundHref="/questions"
      editing={editing}
      onEditingChange={(next) => {
        setEditing(next);
        // Leaving edit by ANY path (Cancel, back, a breadcrumb) must clear
        // `dirty` — not just the `onSaved` success path below. Otherwise a
        // dirty-then-discarded session leaves `dirty` stuck true, and the
        // NEXT edit session (freshly remounted, genuinely pristine) still
        // trips the discard guard on its first Cancel.
        if (!next) setDirty(false);
      }}
      editCap="manage:DiscoveryQuestion"
      dirty={dirty}
      actions={headerActions}
      readView={readView}
      editView={
        <QuestionEdit
          id={id}
          onDirtyChange={setDirty}
          onSaved={() => {
            setDirty(false);
            setEditing(false);
            refresh();
          }}
        />
      }
    />
  );
}
